// src/components/jobPost/steps/Step6Review.jsx
import { useState, useEffect } from "react";
import { useJobPostProgressContext } from "../../../../context/jobPostProgressFrom";
import { Card, CardContent } from "../../../ui/card";
import { StepFooterNav } from "../../shared/NavigationFooter";
import {
  MapPin,
  Wrench,
  Clock,
  Calendar as CalendarIcon,
  DollarSign,
  CheckSquare,
  CheckCircle2,
  AlertTriangle,
  User,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { createJob } from "../../../../apiManager/houseOwner/createJob";
import { setCredentials } from "../../../../slices/authSlice";
import { removeJobData, setJobData } from "../../../../slices/jobSlice";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../../../ui/dialog";
import { Button } from "../../../ui/button";
import { isScheduled } from "../../../../lib/utils";

export function Step6({ currentStep, totalSteps, nextStep, preStep }) {
  const { jobPostProgress } = useJobPostProgressContext();
  const [submitting, setSubmitting] = useState(false);
  const { userInfo } = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // dialog states
  const [showSignupDialog, setShowSignupDialog] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [errorDialog, setErrorDialog] = useState({ open: false, message: "" });

  const isBeingScheduled = isScheduled(jobPostProgress.scheduledDate, (new Date()).getTime());

  // --- Validation ---
  const validateFields = () => {
    const newErrors = {};
    if (!jobPostProgress.location?.address) {
      newErrors.location = "Please provide a valid location.";
    }
    if (!jobPostProgress.scheduledTime) {
      newErrors.scheduledTime = "Please select a scheduled time.";
    }
    if (!jobPostProgress.serviceDuration) {
      newErrors.serviceDuration = "Please specify the service duration.";
    }
    if (!jobPostProgress.selectedService?.service) {
      newErrors.services = "Please select a service.";
    }
    if (
      !jobPostProgress.selectedService?.serviceEquipment ||
      jobPostProgress.selectedService.serviceEquipment.length === 0
    ) {
      newErrors.serviceEquipment = "Please select at least one equipment.";
    }
    if (!jobPostProgress.selectedService?.serviceInstruction) {
      newErrors.serviceInstruction = "Please provide service instructions.";
    }
    if (!jobPostProgress.paymentOffering || jobPostProgress.paymentOffering <= 0) {
      newErrors.paymentOffering = "Please provide a valid payment offering.";
    }
    return Object.keys(newErrors).length === 0;
  };

  // --- Submit Handler ---
  const handleSubmit = async () => {
    if (!validateFields()) {
      setErrorDialog({
        open: true,
        message: "Please fill in all required fields before posting.",
      });
      return;
    }

    setSubmitting(true);
    try {
      const paymentAmountInCents = Math.round(
        jobPostProgress.paymentOffering * 100
      );

      // scheduledDate is stored as a Toronto "yyyy-mm-dd" string from Step5.
      // If it somehow arrives as a Date object, format it in Toronto time to
      // avoid the device's local-timezone offset shifting the date by one day.
      const scheduledDate = jobPostProgress.scheduledDate;
      let scheduledDateStr;
      if (typeof scheduledDate === 'string') {
        scheduledDateStr = scheduledDate;
      } else {
        // Fallback: extract yyyy-mm-dd in Toronto timezone (en-CA gives "yyyy-mm-dd")
        scheduledDateStr = scheduledDate.toLocaleDateString("en-CA", {
          timeZone: "America/Toronto",
        });
      }

      const jobData = {
        serviceDetail: jobPostProgress.selectedService,
        location: {
          type: "Point",
          coordinates: [jobPostProgress.location.lng, jobPostProgress.location.lat],
          address: jobPostProgress.location.address
        },
        completionTime: jobPostProgress.serviceDuration,
        scheduledTime: jobPostProgress.scheduledTime,
        scheduledDate: scheduledDateStr,
        isRequestedForVolunteer: jobPostProgress.isRequestedForVolunteer,
        referredBy:
          jobPostProgress.referredBy === "" ? null : jobPostProgress.referredBy,
        paymentInfo: {
          amount: paymentAmountInCents,
        },
        window: jobPostProgress.window,
        jobStatus: isScheduled(jobPostProgress.scheduledDate, (new Date()).getTime()) ? "scheduled" : "open",
        isScheduled: isScheduled(jobPostProgress.scheduledDate, (new Date()).getTime())
      };

      if (userInfo) {
        console.log("Job Data", jobData);
        const res = await createJob(userInfo.user.id, jobData);
        if (res.error) {
          setErrorDialog({
            open: true,
            message: res.error || "Failed to create job.",
          });
        } else {
          dispatch(setCredentials({ ...res }));
          dispatch(removeJobData());
          setShowSuccessDialog(true);
        }
      } else {
        dispatch(setJobData(jobData));
        setShowSignupDialog(true);
      }
    } catch (err) {
      console.log(err);
      setErrorDialog({
        open: true,
        message: "Something went wrong while posting your job. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  // --- UI for review items ---
  const reviewItem = (title, icon, value) => (
    <div className="space-y-1">
      <h3 className="text-sm font-medium text-gray-500">{title}</h3>
      <p className="flex items-start gap-3 text-lg text-gray-900">
        <span className="relative top-1">{icon}</span>
        {value}
      </p>
    </div>
  );

  // --- Info Dialog Logic ---
  const [showInfoDialog, setShowInfoDialog] = useState(false);
  const [infoMessage, setInfoMessage] = useState("");

  // useEffect(() => {
  //   if (jobPostProgress.scheduledDate && jobPostProgress.scheduledTime) {
  //     const now = new Date();
  //     let scheduledDate = new Date(jobPostProgress.scheduledDate);

  //     // Ensure specific time is set on scheduledDate
  //     let hour = parseInt(jobPostProgress.scheduledTime.hour);
  //     if (jobPostProgress.scheduledTime.period === 'PM' && hour !== 12) hour += 12;
  //     if (jobPostProgress.scheduledTime.period === 'AM' && hour === 12) hour = 0;

  //     scheduledDate.setHours(hour, parseInt(jobPostProgress.scheduledTime.minute), 0, 0);

  //     const diffMs = scheduledDate.getTime() - now.getTime();
  //     const diffHours = diffMs / (1000 * 60 * 60);

  //     // Check if next day
  //     const tomorrow = new Date(now);
  //     tomorrow.setDate(tomorrow.getDate() + 1);
  //     const isNextDay = scheduledDate.getDate() === tomorrow.getDate() &&
  //       scheduledDate.getMonth() === tomorrow.getMonth() &&
  //       scheduledDate.getFullYear() === tomorrow.getFullYear();

  //     if (diffHours < 24) {
  //       setInfoMessage("If scheduled service is next day and less than 24 hours, it may take 30 mins for posting to appear");
  //       setShowInfoDialog(true);
  //     } else if (diffHours > 24) {
  //       setInfoMessage("Posting for helpers to accept, 24 hours before your service time.");
  //       setShowInfoDialog(true);
  //     }
  //   }
  // }, []);

  useEffect(() => {
    if (!jobPostProgress.scheduledDate || !jobPostProgress.scheduledTime) return;

    const now = new Date();

    const scheduledDateTime = new Date(jobPostProgress.scheduledDate);

    let hour = parseInt(jobPostProgress.scheduledTime.hour, 10);
    const minute = parseInt(jobPostProgress.scheduledTime.minute, 10);

    if (jobPostProgress.scheduledTime.period === 'PM' && hour !== 12) hour += 12;
    if (jobPostProgress.scheduledTime.period === 'AM' && hour === 12) hour = 0;

    scheduledDateTime.setHours(hour, minute, 0, 0);

    // Normalize dates for comparison (ignore time)
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const scheduledDayOnly = new Date(scheduledDateTime);
    scheduledDayOnly.setHours(0, 0, 0, 0);

    // SAME-DAY service → no popup
    if (scheduledDayOnly.getTime() === today.getTime()) {
      setShowInfoDialog(false);
      return;
    }

    // Only SCHEDULED service (future date)
    if (scheduledDayOnly.getTime() > today.getTime()) {
      const diffMs = scheduledDateTime.getTime() - now.getTime();
      const diffHours = diffMs / (1000 * 60 * 60);

      if (diffHours < 24) {
        setInfoMessage(
          "If scheduled service is next day and less than 24 hours, it may take 30 mins for posting to appear"
        );
        setShowInfoDialog(true);
      } else {
        setInfoMessage(
          "Posting for helpers to accept, 24 hours before your service time."
        );
        setShowInfoDialog(true);
      }
    }
  }, [jobPostProgress.scheduledDate, jobPostProgress.scheduledTime]);


  return (
    <div className="space-y-12 py-5 md:py-10">
      {/* Title */}
      <div className="text-center space-y-4">
        <h1 className="text-3xl md:text-5xl font-bold text-black">
          Review Your Request
        </h1>
        <p className="text-xl text-gray-600 max-w-2xl mx-auto">
          Please review all details before posting your job
        </p>
      </div>

      {/* Review Card */}
      <div className="max-w-xl mx-auto">
        <Card className="p-8">
          <CardContent className="p-0 space-y-8">
            {reviewItem(
              "Location",
              <MapPin className="w-5 h-5 text-gray-500" />,
              jobPostProgress.location?.address || "Not specified"
            )}
            {reviewItem(
              "Service Required",
              <Wrench className="w-5 h-5 text-gray-500" />,
              jobPostProgress.selectedService.service || "Not selected"
            )}
            {reviewItem(
              "Service Start Time",
              <Clock className="w-5 h-5 text-gray-500" />,
              jobPostProgress.scheduledTime
                ? `${jobPostProgress.scheduledTime.hour}:${jobPostProgress.scheduledTime.minute} ${jobPostProgress.scheduledTime.period}`
                : "Not specified"
            )}
            {reviewItem(
              "Service Window",
              <Clock className="w-5 h-5 text-gray-500" />,
              jobPostProgress.window || "Not specified"
            )}
            {reviewItem(
              "Approximate Duration",
              <Clock className="w-5 h-5 text-gray-500" />,
              jobPostProgress.serviceDuration || "Not specified"
            )}
            {reviewItem(
              "Equipment Provided",
              <CheckSquare className="w-5 h-5 text-gray-500" />,
              Array.isArray(jobPostProgress.selectedService.serviceEquipment) &&
                jobPostProgress.selectedService.serviceEquipment.length > 0
                ? jobPostProgress.selectedService.serviceEquipment.join(", ")
                : "Not selected"
            )}
            {reviewItem(
              "Payment Offering",
              <DollarSign className="w-5 h-5 text-gray-500" />,
              jobPostProgress.paymentOffering
                ? `$${jobPostProgress.paymentOffering}`
                : "Not specified"
            )}
            {reviewItem(
              "Scheduled Date",
              <CalendarIcon className="w-5 h-5 text-gray-500" />,
              jobPostProgress.scheduledDate
                ? new Date(jobPostProgress.scheduledDate).toLocaleDateString(
                  "en-US",
                  { year: "numeric", month: "short", day: "numeric" }
                )
                : "Not selected"
            )}
          </CardContent>
        </Card>
      </div>

      {/* Footer Nav */}
      <StepFooterNav
        currentStep={currentStep}
        totalSteps={totalSteps}
        onBack={preStep}
        onNext={handleSubmit}
        nextDisabled={submitting}
        finishText={submitting ? "Submitting..." : (isBeingScheduled ? 'Schedule Job' : 'Next')}
      />

      {/* --- Info Dialog --- */}
      <Dialog open={showInfoDialog} onOpenChange={setShowInfoDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="w-6 h-6 text-blue-500" />
              Scheduling Information
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-gray-700 text-center text-lg">{infoMessage}</p>
          </div>
          <DialogFooter>
            <Button onClick={() => setShowInfoDialog(false)} className="w-full">
              Got it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* --- Signup Required Dialog --- */}
      <Dialog open={showSignupDialog} onOpenChange={setShowSignupDialog}>
        <DialogContent className="sm:max-w-md">
          <div className="flex flex-col items-center text-center space-y-6 p-2">
            <div className="flex flex-col items-center space-y-4">
              <div className="flex items-center justify-center w-16 h-16 rounded-full bg-blue-50">
                <User className="w-8 h-8 text-blue-600" />
              </div>
              <DialogHeader>
                <DialogTitle className="text-xl text-center font-semibold">Signup Required</DialogTitle>
                <DialogDescription className="text-sm text-center text-muted-foreground pt-2">
                  Your job has been saved, but you need to sign up to post it.
                </DialogDescription>
              </DialogHeader>
            </div>

            <DialogFooter className="w-full">
              <Button
                onClick={() => navigate("/homeowner/profile")}
                className="w-full"
              >
                Continue to Signup
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* --- Success Dialog --- */}
      <Dialog open={showSuccessDialog} onOpenChange={setShowSuccessDialog}>
        <DialogContent className="sm:max-w-md">
          <div className="flex flex-col items-center text-center space-y-6 p-2">
            <div className="flex flex-col items-center space-y-4">
              <div className="flex items-center justify-center w-16 h-16 rounded-full bg-green-50">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              </div>
              <DialogHeader className="text-center">
                <DialogTitle className="text-xl text-center font-semibold">Job {isBeingScheduled ? 'Scheduled' : 'Posted'} Successfully!</DialogTitle>
                <DialogDescription className="text-sm text-center text-muted-foreground pt-2">
                  Note: Your job is not published yet and requires preauthorization or  payment confirmation.
                </DialogDescription>
              </DialogHeader>
            </div>

            <DialogFooter className="flex flex-col gap-3 w-full sm:flex-row">
              <Button
                onClick={() => window.location.reload()}
                className="w-full sm:flex-1"
              >
                Post Another Job
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate("/homeowner/jobs?type=unpaid")}
                className="w-full sm:flex-1"
              >
                View My Jobs
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* --- Error Dialog --- */}
      <Dialog open={errorDialog.open} onOpenChange={(o) => setErrorDialog({ ...errorDialog, open: o })}>
        <DialogContent>
          <DialogHeader>
            <AlertTriangle className="w-10 h-10 text-red-500 mb-2" />
            <DialogTitle>Error</DialogTitle>
          </DialogHeader>
          <p className="text-gray-700">{errorDialog.message}</p>
          <DialogFooter>
            <Button onClick={() => setErrorDialog({ open: false, message: "" })}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
